import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../ui/Icon'
import { displayDate } from '../../../../../utils/dateUtils'
import { InputDate } from '../../../../input/inputDate/InputDate'
import { SelectOptionInput } from '../../../../input/selectOptionInput/SelectOptionInput'
import {
  ReportIntervalInput,
  ReportPeriod,
} from '../../../../../types/report/report'

export type ReportPeriodOption = {
  value: ReportPeriod
  label: string
  shortLabel: string
}

type ReportFiltersProps = {
  value: ReportIntervalInput
  periodOptions: ReportPeriodOption[]
  onChange: (value: ReportIntervalInput) => void
}

export const ReportFilters = ({
  value,
  periodOptions,
  onChange,
}: ReportFiltersProps) => {
  const { t } = useTranslation('reports')
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false)
  const { period, startDate, endDate } = value
  const dateRangePartiallySelected =
    period === ReportPeriod.CUSTOM && Boolean(startDate) !== Boolean(endDate)
  const dateRangeOrderInvalid = Boolean(
    hasInvalidEndDate || (startDate && endDate && endDate < startDate)
  )
  const dateRangeInvalid = dateRangePartiallySelected || dateRangeOrderInvalid
  const startDateMissing = dateRangePartiallySelected && !startDate
  const endDateMissing = dateRangePartiallySelected && !endDate

  const handlePeriodChange = (nextPeriod: ReportPeriod) => {
    setHasInvalidEndDate(false)

    onChange({
      period: nextPeriod,
      startDate: nextPeriod === ReportPeriod.CUSTOM ? startDate : null,
      endDate: nextPeriod === ReportPeriod.CUSTOM ? endDate : null,
    })
  }

  const handleStartDateChange = (nextStartDate: Date | null) => {
    setHasInvalidEndDate(false)

    onChange({
      period: ReportPeriod.CUSTOM,
      startDate: nextStartDate,
      endDate:
        nextStartDate && endDate && endDate < nextStartDate ? null : endDate,
    })
  }

  const handleEndDateChange = (nextEndDate: Date | null, isValid: boolean) => {
    if (!isValid) {
      setHasInvalidEndDate(true)
      return
    }

    setHasInvalidEndDate(false)

    onChange({
      ...value,
      period: ReportPeriod.CUSTOM,
      endDate: nextEndDate,
    })
  }

  return (
    <div className="reports-filters">
      {period === ReportPeriod.CUSTOM && (
        <div className="reports-filter-section reports-filter-date">
          {dateRangeInvalid && (
            <div className="reports-date-range-helper" role="alert">
              <Icon name="info" size={15} />
              <span>
                {t(
                  dateRangeOrderInvalid
                    ? 'reports.filters.invalid-date-range'
                    : 'reports.filters.date-range-required'
                )}
              </span>
            </div>
          )}

          <div className="reports-filter-control">
            <label htmlFor="reports-start-date">
              {t('reports.filters.start-date')}
            </label>
            <InputDate
              id="reports-start-date"
              name="startDate"
              value={startDate}
              invalid={startDateMissing}
              disablePastDates={false}
              disableFutureDates
              onChange={(_, nextValue) => handleStartDateChange(nextValue)}
            />
          </div>
          <div className="reports-filter-control">
            <label htmlFor="reports-end-date">
              {t('reports.filters.end-date')}
            </label>
            <InputDate
              id="reports-end-date"
              name="endDate"
              value={endDate}
              invalid={endDateMissing || dateRangeOrderInvalid}
              disablePastDates={false}
              disableFutureDates
              onValidate={
                startDate ? value => value >= displayDate(startDate) : undefined
              }
              onChange={(_, nextValue, isValid) =>
                handleEndDateChange(nextValue, isValid)
              }
            />
          </div>
        </div>
      )}

      <div className="reports-filter-section reports-filter-period">
        <div className="reports-filter-control">
          <label htmlFor="reports-period">{t('reports.filters.period')}</label>
          <SelectOptionInput
            id="reports-period"
            name="period"
            value={period}
            list={periodOptions}
            className="reports-period-select"
            required
            isSearchable={false}
            onChange={(_, nextValue) =>
              handlePeriodChange(nextValue as ReportPeriod)
            }
          />
        </div>
      </div>
    </div>
  )
}
